import React, { useEffect, useState, useCallback, useMemo } from "react";
import MaterialTable from "material-table";
import { useStyles } from "./styles";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import { MTableToolbar } from "material-table";
import ImageLoader from "react-load-image";
import {
  getRolesRequest,
  getUsersForAdminRequest,
  getAyucareUsersRequest,
} from "../../UsersApiActions";
import Preloader from "../../../../utilities/components/Preloader/Preloader";
import ActiveStatusIcon from "../../../../utilities/components/ActiveStatusIcon/ActiveStatusIcon";
import {
  PAGE_SIZE,
  PAGE_SIZE_OPTIONS,
  ROLES,
} from "../../../../utilities/constants";
import { getUserInfo } from "../../../../utilities/storage";
import { debounce } from "../../../../utilities/utilityFunctions";
import defaultUser from "../../../../assets/user.png";
import UserFormDialog from "../AyucareAdminForm/AyucareAdminForm";
import {
  Box,
  Button,
  FormControl,
  Icon,
  MenuItem,
  Select,
  Typography,
  TextField,
  InputLabel,
} from "@material-ui/core";

const Users = ({
  getAllRoles,
  roles,
  getAllUsers,
  getUsersForAdmin,
  users,
}) => {
  const classes = useStyles();
  const role = getUserInfo().Roles[0].name;

  const [localFilters, setLocalFilters] = useState({
    name: "",
    email: "",
    phone: "",
    role: "",
    enabled: "",
  });

  const [apiParams, setApiParams] = useState({
    page: 0,
    rowsPerPage: PAGE_SIZE,
    searchData: {
      name: "",
      email: "",
      phone: "",
      role: "",
      enabled: "",
    },
    isHideLoader: false,
  });

  const [openUserFormDialog, setOpenUserFormDialog] = useState(false);
  const [refreshList, setRefreshList] = useState(false);

  const updateApiParams = useCallback(
    debounce((filters) => {
      setApiParams((prev) => ({
        ...prev,
        searchData: filters,
        page: 0,
      }));
    }, 400),
    []
  );

  const handleFilterChange = (field) => (e) => {
    const value = e.target.value;
    const newFilters = {
      ...localFilters,
      [field]: value,
    };
    setLocalFilters(newFilters);
    updateApiParams(newFilters);
  };

  const handleSelectChange = (field) => (e) => {
    const value = e.target.value;
    const newFilters = {
      ...localFilters,
      [field]: value,
    };
    setLocalFilters(newFilters);
    setApiParams((prev) => ({
      ...prev,
      searchData: newFilters,
      page: 0,
    }));
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (role === ROLES.ROLE_SYSTEM_ADMIN) {
          await getAllUsers(
            {
              page: apiParams.page,
              rowsPerPage: apiParams.rowsPerPage,
              searchData: apiParams.searchData,
            },
            apiParams.isHideLoader
          );
        } else {
          await getUsersForAdmin(
            {
              page: apiParams.page,
              rowsPerPage: apiParams.rowsPerPage,
              searchData: apiParams.searchData,
            },
            apiParams.isHideLoader
          );
        }
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    };

    fetchData();
  }, [apiParams, getAllUsers, getUsersForAdmin, role]);

  useEffect(() => {
    getAllRoles();
    setApiParams((prev) => ({ ...prev, isHideLoader: true }));
  }, [getAllRoles]);

  useEffect(() => {
    if (refreshList && role === ROLES.ROLE_SYSTEM_ADMIN) {
      getAllUsers({
        page: apiParams.page,
        rowsPerPage: apiParams.rowsPerPage,
        searchData: apiParams.searchData,
      }).then(() => setRefreshList(false));
    }
  }, [refreshList, apiParams, getAllUsers, role]);

  const columns = useMemo(
    () => [
      {
        field: "name",
        title: "Name",
        filtering: true,
        defaultFilter: localFilters.name,
        cellStyle: { width: "25%" },
        headerStyle: { width: "25%" },
        render: ({ imageUrl, name, id }) => (
          <Box
            className={classes.profileContainer}
            component={Link}
            to={`users/${id}`}
          >
            <Box className={classes.profileInnerContainer}>
              <ImageLoader src={imageUrl || defaultUser}>
                <img alt={id} className={classes.profileImage} />
                <div className={classes.profileImageError}>{id}</div>
                <Preloader alt={id} className={classes.profileImage} />
              </ImageLoader>
            </Box>
            <Box className={classes.nameContainer}>
              <Typography classes={{ root: classes.name }}>{name}</Typography>
            </Box>
          </Box>
        ),
      },
      {
        field: "email",
        title: "Email",
        filtering: true,
        defaultFilter: localFilters.email,
        cellStyle: { width: "25%" },
        headerStyle: { width: "25%" },
      },
      {
        field: "phone",
        title: "Phone",
        filtering: true,
        defaultFilter: localFilters.phone,
        cellStyle: { width: "20%" },
        headerStyle: { width: "20%" },
      },
      {
        field: "Roles",
        title: "Role",
        filtering: true,
        filterComponent: () => (
          <FormControl fullWidth>
            <Select
              value={localFilters.role}
              onChange={handleSelectChange("role")}
            >
              <MenuItem value="">All</MenuItem>
              {roles?.map((role) => (
                <MenuItem key={role.id} value={role.name}>
                  {role.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ),
        cellStyle: { width: "20%" },
        headerStyle: { width: "20%" },
        render: ({ Roles }) => <p>{Roles}</p>,
      },
      {
        field: "orgs",
        title: "Organization",
        filtering: false,
        cellStyle: { width: "20%" },
        headerStyle: { width: "20%" },
        render: (rowData) => {
          const orgNames =
            (rowData.orgs?.length
              ? rowData.orgs.map((org) => org.name)
              : rowData.Admin?.Organization?.length
              ? rowData.Admin.Organization.map((org) => org.name)
              : []) || [];

          return <p>{orgNames.length > 0 ? orgNames.join(", ") : "-"}</p>;
        },
      },
      {
        field: "enabled",
        title: "Active Status",
        filtering: true,
        filterComponent: () => (
          <FormControl fullWidth>
            <Select
              value={localFilters.enabled}
              onChange={handleSelectChange("enabled")}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="true">Active</MenuItem>
              <MenuItem value="false">Blocked</MenuItem>
            </Select>
          </FormControl>
        ),
        cellStyle: { width: "10%", textAlign: "center" },
        headerStyle: { width: "10%", textAlign: "center" },
        render: ({ enabled, isBanned }) => (
          <ActiveStatusIcon
            enabled={enabled}
            isBanned={isBanned}
            isLabel={true}
          />
        ),
      },
    ],
    [classes, localFilters, roles]
  );

  const FilterComponent = useMemo(
    () => (
      <Box
        display="flex"
        gap={3}
        mb={2}
        sx={{
          "& .MuiFormControl-root, & .MuiTextField-root": {
            marginRight: 40,
          },
        }}
        style={{ padding: "0 16px", flexWrap: "wrap" }}
      >
        <TextField
          label="Search Name"
          variant="outlined"
          size="small"
          value={localFilters.name}
          onChange={handleFilterChange("name")}
        />
        <TextField
          label="Search Email"
          variant="outlined"
          size="small"
          value={localFilters.email}
          onChange={handleFilterChange("email")}
        />
        <TextField
          label="Search Phone"
          variant="outlined"
          size="small"
          value={localFilters.phone}
          onChange={handleFilterChange("phone")}
        />
        <FormControl variant="outlined" size="small" style={{ minWidth: 150 }}>
          <InputLabel>Role</InputLabel>
          <Select
            value={localFilters.role}
            onChange={handleSelectChange("role")}
            label="Role"
          >
            <MenuItem value="">All</MenuItem>
            {roles?.map((role) => (
              <MenuItem key={role.id} value={role.name}>
                {role.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl variant="outlined" size="small" style={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={localFilters.enabled}
            onChange={handleSelectChange("enabled")}
            label="Status"
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="true">Active</MenuItem>
            <MenuItem value="false">Blocked</MenuItem>
          </Select>
        </FormControl>
      </Box>
    ),
    [localFilters, roles]
  );

  return (
    <Box className={classes.outerContainer}>
      <Box>
        <UserFormDialog
          setRefreshList={setRefreshList}
          openUserFormDialog={openUserFormDialog}
          setOpenUserFormDialog={setOpenUserFormDialog}
        />

        {users && (
          <>
            {role === ROLES.ROLE_SYSTEM_ADMIN && (
              <Box className={classes.addButtonContainer}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => setOpenUserFormDialog(true)}
                  startIcon={<Icon>add</Icon>}
                  disableElevation
                  className={classes.addButton}
                >
                  Admin
                </Button>
              </Box>
            )}

            <Box mt={1}>
              {FilterComponent}

              <MaterialTable
                title="Users"
                columns={columns}
                data={users.rows}
                options={{
                  search: false,
                  filtering: false,
                  sorting: false,
                  pageSize: apiParams.rowsPerPage,
                  pageSizeOptions: PAGE_SIZE_OPTIONS,
                }}
                totalCount={users.count}
                page={apiParams.page}
                onChangeRowsPerPage={(pageSize) => {
                  setApiParams((prev) => ({
                    ...prev,
                    rowsPerPage: pageSize,
                  }));
                }}
                onChangePage={(page) => {
                  setApiParams((prev) => ({
                    ...prev,
                    page,
                  }));
                }}
                components={{
                  Toolbar: (props) => <MTableToolbar {...props} />,
                }}
              />
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

const mapStateToProps = (state) => ({
  users: state.ayucareUsers.users,
  roles: state.ayucareUsers.roles,
});

const mapDispatchToProps = {
  getAllUsers: getAyucareUsersRequest,
  getUsersForAdmin: getUsersForAdminRequest,
  getAllRoles: getRolesRequest,
};

export default connect(mapStateToProps, mapDispatchToProps)(React.memo(Users));
